"use client";

import React, { useState, useEffect, useRef, useContext } from "react";
import ApexCharts from "apexcharts";
import { styled, useTheme, ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  CssBaseline,
  useMediaQuery,
  IconButton,
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "../common/components/LocalizationProvider";
import { LightMode, DarkMode } from "@mui/icons-material";
import { utils, writeFile } from "xlsx";

// ---- globals for apex safety ----
declare global {
  interface Window {
    ApexCharts?: { colors?: string[] };
    Apex?: { colors?: string[] };
  }
}

// ---- sizes (top-level so styled() can access) ----
const PIE_HEIGHT = 380;
const PIE_WRAPPER_HEIGHT = 380;
const BAR_HEIGHT = 560;
const BAR_WRAPPER_HEIGHT = 560;

// ---- types ----
interface StateCountsType {
  [key: string]: any[];
}

// ---- color mode context (keeps your toggler) ----
const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

// ---- styled components ----
const Root = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
}));

const Header = styled("header")(({ theme }) => ({
  padding: theme.spacing(1.25, 3),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "transparent",
  color: theme.palette.text.primary,
  boxShadow: "none",
}));

const Content = styled(Container)(({ theme }) => ({
  maxWidth: "100%",
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(4),
  paddingLeft: 0,
  paddingRight: 0,
  flex: 1,
  overflow: "auto",
  backgroundColor: theme.palette.mode === "dark" ? "transparent" : theme.palette.grey[50],
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.25),
  borderRadius: 4,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.mode === "dark" ? "rgba(26,26,26,0.95)" : "#f1f3f5",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}`,
  boxShadow: "none",
  minWidth: 0,
  flex: "1 1 auto",
  height: "100%",
}));

const SmallPieWrapper = styled("div")(({ theme }) => ({
  height: PIE_WRAPPER_HEIGHT,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const PieWrapper = styled("div")(({ theme }) => ({
  height: PIE_WRAPPER_HEIGHT,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const BarWrapper = styled("div")(({ theme }) => ({
  height: BAR_WRAPPER_HEIGHT,
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 800,
  marginBottom: theme.spacing(1),
  textAlign: "left",
  textTransform: "uppercase",
  color: theme.palette.text.primary,
}));

const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
}));

// ---- main component ----
const Dashboard = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const t = useTranslation();
  const isDark = theme.palette.mode === "dark";

  // data states
  const [devices, setDevices] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Menu states
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedChart, setSelectedChart] = useState<string | null>(null);

  // refs for charts
  const pie1Ref = useRef<HTMLDivElement | null>(null);
  const pie2Ref = useRef<HTMLDivElement | null>(null);
  const pie3Ref = useRef<HTMLDivElement | null>(null);
  const bar1Ref = useRef<HTMLDivElement | null>(null);
  const bar2Ref = useRef<HTMLDivElement | null>(null);
  const bar3Ref = useRef<HTMLDivElement | null>(null);

  const lastUpdateRef = useRef(0);

  // store group data for CSV export
  const pieGroupsRef = useRef<{ [k: string]: Record<string, any[]> }>({
    pie1: {},
    pie2: {},
    pie3: {},
  });

  const barGroupsRef = useRef<{ [k: string]: any[] }>({
    bar1: [],
    bar2: [],
    bar3: [],
  });

  // Store chart data for export
  const chartDataRef = useRef<Record<string, any>>({});

  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const textColor = theme.palette.text.primary;

  const colorsArrRef = useRef<string[]>([]);

  // Função para formatar números com separadores de milhar
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  // ---- APPLY EXACT COLORS ARRAY (requested) ----
  useEffect(() => {
    const colorsArr = [
      secondary,
      "#FF4560", // Red
      "#F86624", // Orange
      "#775DD0", // Purple
      "#3F51B5", // Indigo
      "#546E7A", // Blue-gray
      "#D4526E", // Pink
      "#8D5B4C", // Brown
      "#A5978B", // Taupe
      "#4ECDC4", // Teal
      primary,
      "#C7F464", // Lime
      "#81D4FA", // Light Blue
      "#FD6A02", // Tangerine
      "#7B1FA2", // Dark Purple
      "#FFC300", // Mustard
      "#FF5733", // Burnt Orange
      "#C70039", // Crimson
      "#900C3F"]; // Dark Red

    colorsArrRef.current = colorsArr; // Armazena na ref

    (window as any).Apex = (window as any).Apex || {};
    (window as any).Apex.colors = colorsArr;

    if (window.ApexCharts) {
      window.ApexCharts.colors = colorsArr;
    }

    try {
      (ApexCharts as any).defaultOptions = (ApexCharts as any).defaultOptions || {};
      (ApexCharts as any).defaultOptions.colors = colorsArr;
    } catch (e) {
      // ignore
    }
  }, [primary, secondary]);

// ---- fetch devices & positions (keep your endpoints) ----
useEffect(() => {
  const fetchData = async () => {
    try {
      const devResp = await fetch("/api/devices");
      if (!devResp.ok) {
        const errorText = await devResp.text();
        throw new Error(`Erro ao buscar devices: ${devResp.status} - ${errorText}`);
      }

      // Check if response is JSON
      const contentType = devResp.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await devResp.text();
        throw new Error(`Resposta da API não é JSON: ${textResponse.substring(0, 100)}...`);
      }

      const devs = await devResp.json();

      const posResp = await fetch("/api/positions");
      if (!posResp.ok) {
        const errorText = await posResp.text();
        throw new Error(`Erro ao buscar positions: ${posResp.status} - ${errorText}`);
      }

      // Check if response is JSON
      const posContentType = posResp.headers.get("content-type");
      if (!posContentType || !posContentType.includes("application/json")) {
        const textResponse = await posResp.text();
        throw new Error(`Resposta da API não é JSON: ${textResponse.substring(0, 100)}...`);
      }

      const poss = await posResp.json();

      setDevices(devs || []);
      setPositions(poss || []);
      setLoading(false);
    } catch (e) {
      console.error("Erro ao carregar dados:", e);
      setError("Erro ao carregar dados: " + (e instanceof Error ? e.message : String(e)));
      setLoading(false);
    }
  };
  fetchData();
}, []);

  // ---- base options helper ----
  const baseOptions = (overrides = {}) => ({
    chart: { background: "transparent", foreColor: textColor },
    theme: { mode: isDark ? "dark" : "light" },
    tooltip: { theme: isDark ? "dark" : "light" },
    legend: {
      labels: { colors: textColor },
      fontSize: "12px",
    },
    ...overrides,
  });

  // ---- utility: CSV helpers ----
  const escapeCsv = (v: any) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(",") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const downloadCSV = (filename: string, csvContent: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", filename);
    a.click();
    URL.revokeObjectURL(url);
  };

  const groupsObjToArray = (groupsObj: Record<string, any[]>) =>
    Object.keys(groupsObj).map((label) => ({ label, items: groupsObj[label] || [] }));

  const exportGroupsAsCSV = (groupsObj: Record<string, any[]>, filename = "groups.csv") => {
    const groups = groupsObjToArray(groupsObj);
    const rows: string[] = [];
    rows.push(["Group,Device"].map(escapeCsv).join(","));
    groups.forEach((g) => {
      const items = (g.items || []).map((id) => {
        const dev = devices.find((d: any) => d.id === id || d.id === (id as any));
        if (dev) return dev.name || dev.id || id;
        return typeof id === "string" ? id : (id && id.name) || id;
      });
      items.forEach((it) => rows.push([escapeCsv(g.label), escapeCsv(it)].join(",")));
    });
    downloadCSV(filename, rows.join("\n"));
  };

  const exportAllDevices = (filename = "devices.csv") => {
    if (!devices || devices.length === 0) {
      downloadCSV(filename, "id,name,status,lastUpdate\n");
      return;
    }
    // flatten keys (union of keys of objects)
    const header = Array.from(new Set(devices.flatMap((d) => Object.keys(d || {}))));
    const rows = [header.map(escapeCsv).join(",")];
    devices.forEach((d: any) => {
      const line = header.map((h) => escapeCsv(d[h])).join(",");
      rows.push(line);
    });
    downloadCSV(filename, rows.join("\n"));
  };

  // Export chart data functions
  const exportChartCSV = (chartId: string) => {
    const data = chartDataRef.current[chartId];
    if (!data) return;

    let rows: string[] = [];
    if (data.labels && data.series) {
      // Pie chart data
      rows.push("Label,Value");
      data.labels.forEach((label: string, i: number) => {
        rows.push(`${escapeCsv(label)},${data.series[i]}`);
      });
    } else if (data.categories && data.series) {
      // Bar chart data
      rows.push("Category,Value");
      data.categories.forEach((category: string, i: number) => {
        rows.push(`${escapeCsv(category)},${data.series[0].data[i]}`);
      });
    }

    downloadCSV(`${chartId}.csv`, rows.join("\n"));
    setMenuAnchor(null);
  };

  const exportChartXLSX = (chartId: string) => {
    const data = chartDataRef.current[chartId];
    if (!data) return;

    let rows: any[] = [];
    if (data.labels && data.series) {
      // Pie chart data
      rows = data.labels.map((label: string, i: number) => ({
        Label: label,
        Value: data.series[i]
      }));
    } else if (data.categories && data.series) {
      // Bar chart data
      rows = data.categories.map((category: string, i: number) => ({
        Category: category,
        Value: data.series[0].data[i]
      }));
    }

    const ws = utils.json_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, chartId);
    writeFile(wb, `${chartId}.xlsx`);
    setMenuAnchor(null);
  };

  // Handle menu opening
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chartId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedChart(chartId);
  };

  // Handle menu closing
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedChart(null);
  };

  // ---- TOP PIES (3) ----
  useEffect(() => {
    if (!pie1Ref.current || !pie2Ref.current || !pie3Ref.current || loading) return;

    // PIE 1 - Last update grouping
    const stateCounts: StateCountsType = { Online: [], "12h": [], "24h": [], "36h": [], "48h": [], "+48h": [] };
    devices.forEach((d: any) => {
      const last = new Date().getTime() - new Date(d.lastUpdate).getTime();
      if (d.status === "online") stateCounts.Online.push(d.id ?? d.name ?? d);
      else if (last < 12 * 3600 * 1000) stateCounts["12h"].push(d.id ?? d.name ?? d);
      else if (last < 24 * 3600 * 1000) stateCounts["24h"].push(d.id ?? d.name ?? d);
      else if (last < 36 * 3600 * 1000) stateCounts["36h"].push(d.id ?? d.name ?? d);
      else if (last < 48 * 3600 * 1000) stateCounts["48h"].push(d.id ?? d.name ?? d);
      else stateCounts["+48h"].push(d.id ?? d.name ?? d);
    });

    // save for export
    pieGroupsRef.current.pie1 = stateCounts;

    const p1Keys = Object.keys(stateCounts);
    const p1Series = p1Keys.map((k) => (stateCounts[k] || []).length);

    // Store chart data for export
    chartDataRef.current.pie1 = {
      labels: p1Keys,
      series: p1Series
    };

    const pie1 = new ApexCharts(
      pie1Ref.current,
      {
        ...baseOptions({
          chart: { type: "donut", height: PIE_HEIGHT, width: "100%" },
          series: p1Series,
          labels: p1Keys,
          plotOptions: { pie: { donut: { size: "70%" } } },
          dataLabels: { enabled: true, formatter: (v: any) => Math.round(v) + "%" },
          legend: { position: isMobile ? "bottom" : "right", fontSize: "12px" },
          tooltip: {
            custom: ({ seriesIndex }: any) => {
              const key = p1Keys[seriesIndex];
              const ids = stateCounts[key] || [];
              const names = ids
                .map((id) => {
                  const dev = devices.find((d: any) => d.id === id);
                  if (dev) return dev.name || dev.id || id;
                  return typeof id === "string" ? id : id?.name || id;
                })
                .filter(Boolean);
              if (names.length === 0) return `<div style="padding:8px">Nenhum device</div>`;
                return `<div style="padding:8px; max-height:260px; overflow:auto; font-size:12px; line-height:1.3;">
                          <b>${key} (${names.length})</b>
                          <ul style="margin:6px 0 0 12px; padding:0; list-style: disc;">
                            ${names.map((n) => `<li>${n}</li>`).join("")}
                          </ul>
                        </div>`;
            },
          },
        }),
      }
    );
    pie1.render();

    // PIE 2 - Movement
    const mCounts: StateCountsType = { movendo: [], parado: [], ralenti: [] };
    positions.forEach((p: any) => {
      const devId = p.deviceId ?? (p.device && (p.device.id ?? p.device));
      if (p.attributes && p.attributes.motion) mCounts.movendo.push(devId);
      else if (p.attributes && p.attributes.ignition && !p.speed) mCounts.ralenti.push(devId);
      else mCounts.parado.push(devId);
    });

    pieGroupsRef.current.pie2 = mCounts;

    const p2Keys = ["movendo", "parado", "ralenti"];
    const p2Series = p2Keys.map((k) => (mCounts[k] || []).length);

    // Store chart data for export
    chartDataRef.current.pie2 = {
      labels: ["Em movimento", "Desligado", "Ocioso"],
      series: p2Series
    };

    const pie2 = new ApexCharts(
      pie2Ref.current,
      {
        ...baseOptions({
          chart: { type: "donut", height: PIE_HEIGHT, width: "100%" },
          series: p2Series,
          labels: ["Em movimento", "Desligado", "Ocioso"],
          plotOptions: { pie: { donut: { size: "70%" } } },
          dataLabels: { enabled: true, formatter: (v: any) => Math.round(v) + "%" },
          legend: { position: isMobile ? "bottom" : "right", fontSize: "12px" },
          tooltip: {
            custom: ({ seriesIndex }: any) => {
              const key = p2Keys[seriesIndex];
              const ids = mCounts[key] || [];
              const names = ids
                .map((id) => {
                  const dev = devices.find((d: any) => d.id === id);
                  if (dev) return dev.name || dev.id || id;
                  return typeof id === "string" ? id : id?.name || id;
                })
                .filter(Boolean);
              if (names.length === 0) return `<div style="padding:8px">Nenhum device</div>`;
                return `<div style="padding:8px; max-height:260px; overflow:auto; font-size:12px; line-height:1.3;">
                          <b>${key} (${names.length})</b>
                          <ul style="margin:6px 0 0 12px; padding:0; list-style: disc;">
                            ${names.map((n) => `<li>${n}</li>`).join("")}
                          </ul>
                        </div>`;
            },
          },
        }),
      }
    );
    pie2.render();

    // PIE 3 - Alarms
    const alarmCounts: StateCountsType = {};
    alarmCounts["Normal"] = [];
    positions.forEach((p: any) => {
      if (p.attributes && p.attributes.alarm) {
        const a = p.attributes.alarm;
        alarmCounts[a] = alarmCounts[a] || [];
        alarmCounts[a].push(p.deviceId);
      } else {
        alarmCounts["Normal"].push(p.deviceId);
      }
    });

    pieGroupsRef.current.pie3 = alarmCounts;

    const p3Keys = Object.keys(alarmCounts);
    const p3Series = p3Keys.map((k) => (alarmCounts[k] || []).length);

    // Store chart data for export
    chartDataRef.current.pie3 = {
      labels: p3Keys,
      series: p3Series
    };

    const pie3 = new ApexCharts(
      pie3Ref.current,
      {
        ...baseOptions({
          chart: { type: "donut", height: PIE_HEIGHT, width: "100%" },
          series: p3Series,
          labels: p3Keys,
          plotOptions: { pie: { donut: { size: "70%" } } },
          dataLabels: { enabled: true, formatter: (v: any) => Math.round(v) + "%" },
          legend: { position: isMobile ? "bottom" : "right", fontSize: "12px" },
          tooltip: {
            custom: ({ seriesIndex }: any) => {
              const key = p3Keys[seriesIndex];
              const ids = alarmCounts[key] || [];
              const names = ids
                .map((id) => {
                  const dev = devices.find((d: any) => d.id === id);
                  if (dev) return dev.name || dev.id || id;
                  return typeof id === "string" ? id : id?.name || id;
                })
                .filter(Boolean);
              if (names.length === 0) return `<div style="padding:8px">Nenhum device</div>`;
                return `<div style="padding:8px; max-height:260px; overflow:auto; font-size:12px; line-height:1.3;">
                          <b>${key} (${names.length})</b>
                          <ul style="margin:6px 0 0 12px; padding:0; list-style: disc;">
                            ${names.map((n) => `<li>${n}</li>`).join("")}
                          </ul>
                        </div>`;
            },
          },
        }),
      }
    );
    pie3.render();

    return () => {
      try {
        pie1.destroy();
        pie2.destroy();
        pie3.destroy();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, positions, loading, isMobile, theme]);

  // ---- BOTTOM BARS (3) ----
  useEffect(() => {
    const currentTime = Date.now(); // Renamed from 'now' to 'currentTime'
    if (currentTime - lastUpdateRef.current < 1000) { // Debounce to 1 second
      return;
    }
    lastUpdateRef.current = currentTime;

    if (!bar1Ref.current || !bar2Ref.current || !bar3Ref.current || loading) return;

    // BAR 1 - Odometer
    const getOdom = (p: any, d: any) =>
      Math.round(
        ((d && d.attributes && !d.attributes["report.ignoreOdometer"] && p.attributes && p.attributes.odometer) ||
          (p.attributes && p.attributes.totalDistance) ||
          0) / 1000
      );

    // Instead of slicing to 20, consider a lower limit
    const odData = positions
      .filter((p) => getOdom(p, devices.find((d) => d.id === p.deviceId)) > 0)
      .sort((a, b) => getOdom(b, devices.find((d) => d.id === b.deviceId)) - getOdom(a, devices.find((d) => d.id === a.deviceId)))
      .slice(0, 10); // Reduce from 20 to 10

    const odSeries = [{ name: "Km", data: odData.map((p) => getOdom(p, devices.find((d) => d.id === p.deviceId))) }];
    const odCats = odData.map((p) => devices.find((d) => d.id === p.deviceId)?.name || "");

    barGroupsRef.current.bar1 = odData.map((p) => devices.find((d) => d.id === p.deviceId)?.name || p.deviceId);

    // Store chart data for export
    chartDataRef.current.bar1 = {
      categories: odCats,
      series: odSeries
    };

    // BAR 1 - Odometer (modifique a criação do gráfico)
    const bar1 = new ApexCharts(
      bar1Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "bar",
            height: BAR_HEIGHT,
            width: "100%",
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "85%",
              borderRadius: 5,
              distributed: true,
              columnWidth: "90%"
            }
          },
          series: odSeries,
          xaxis: {
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            show: false
          },
          grid: {
            show: false
          },
          dataLabels: {
            enabled: false // Alterado para false para remover os labels
          },
          colors: colorsArrRef.current,
          tooltip: {
            enabled: true // Você pode manter o tooltip habilitado para ver informações ao passar o mouse
          }
        }),
      }
    );
    bar1.render();

    // BAR 2 - Hours
    const getHours = (p: any) => (p.attributes && p.attributes.hours ? Math.round(p.attributes.hours / 1000 / 3600) : 0);
    const hrData = positions.filter((p) => getHours(p) > 0).sort((a, b) => getHours(b) - getHours(a)).slice(0, 10); // Reduce from 20 to 10
    const hrSeries = [{ name: "Horas", data: hrData.map((p) => getHours(p)) }];
    const hrCats = hrData.map((p) => devices.find((d) => d.id === p.deviceId)?.name || "");

    barGroupsRef.current.bar2 = hrData.map((p) => devices.find((d) => d.id === p.deviceId)?.name || p.deviceId);

    // Store chart data for export
    chartDataRef.current.bar2 = {
      categories: hrCats,
      series: hrSeries
    };

    // BAR 2 - Hours (modifique a criação do gráfico)
    const bar2 = new ApexCharts(
      bar2Ref.current,
      {
        ...baseOptions({
          chart: {
            type: "bar",
            height: BAR_HEIGHT,
            width: "100%",
            toolbar: { show: false }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "85%",
              borderRadius: 6,
              distributed: true,
              columnWidth: "90%"
            }
          },
          series: hrSeries,
          xaxis: {
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
          },
          yaxis: {
            show: false
          },
          grid: {
            show: false
          },
          dataLabels: {
            enabled: hrData.length <= 20, // Only enable if 10 or fewer items
            formatter: function(val: any, opt: any) {
              return `${hrCats[opt.dataPointIndex] || ""}: ${formatNumber(val)} h`;
            },
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
              colors: ["#fff"]
            },
            offsetX: 10,
            dropShadow: {
              enabled: true,
              top: 1,
              left: 1,
              blur: 1,
              opacity: 0.45
            }
          },
          colors: colorsArrRef.current,
          tooltip: {
            enabled: false
          }
        }),
      }
    );
    bar2.render();

  // BAR 3 - Activity (rangeBar)
  const currentDate = new Date(); // Renamed from 'now' to 'currentDate'
  const from = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
  const to = from + 1000 * 60 * 60 * 24;
  const _devices = [...devices].slice(0, 20); // Reduce from 30 to 10

  // BAR 3 - Activity (rangeBar)
  const chart3 = new ApexCharts(
    bar3Ref.current,
    {
      ...baseOptions({
        chart: { type: "rangeBar", height: BAR_HEIGHT, width: "100%" },
        plotOptions: {
          bar: {
            horizontal: true,
            rangeBarGroupRows: true,
            //barHeight: "60%",
            borderRadius: 6 // Bordas arredondadas como no dash2 copy
          }
        },
        series: [],
        xaxis: {
          min: from,
          max: to,
          type: "datetime",
          labels: {
            style: {
              colors: textColor
            }
          }
        },
        legend: {
          position: isMobile ? "bottom" : "right",
          fontSize: "11px",
          labels: {
            colors: textColor
          }
        },
        grid: {
          show: true, // Mostrar grid como no dash2 copy
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      }),
    }
  );
  chart3.render();

  // fetch trips/stops for activity
  const fetchTripsStops = async () => {
    try {
      const deviceIds = _devices.map((d) => `deviceId=${d.id}`).join("&");
      const dateParam = `&from=${new Date(from).toISOString()}&to=${new Date(to).toISOString()}`;
      const [tripsResp, stopsResp] = await Promise.all([
        fetch(`/api/reports/trips?${deviceIds}${dateParam}`, { headers: { Accept: "application/json" } }),
        fetch(`/api/reports/stops?${deviceIds}${dateParam}`, { headers: { Accept: "application/json" } }),
      ]);
      if (tripsResp.ok && stopsResp.ok) {
        const trips = await tripsResp.json();
        const stops = await stopsResp.json();
        const stopsSeries = {
          color: theme.palette.primary.main,
          name: "Paradas",
          data: stops.map((s: any) => ({ x: _devices.find((d) => d.id === s.deviceId)?.name || "", y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()] })),
        };
        const tripsSeries = {
          color: theme.palette.secondary.main,
          name: "Viagens",
          data: trips.map((s: any) => ({ x: _devices.find((d) => d.id === s.deviceId)?.name || "", y: [new Date(s.startTime).getTime(), new Date(s.endTime).getTime()] })),
        };
        chart3.updateOptions({ series: [stopsSeries, tripsSeries] }, true, true);
        barGroupsRef.current.bar3 = [
          ...(stops || []).map((s: any) => _devices.find((d) => d.id === s.deviceId)?.name || s.deviceId),
          ...(trips || []).map((s: any) => _devices.find((d) => d.id === s.deviceId)?.name || s.deviceId),
        ];
      }
    } catch (e) {
      console.error(e);
    }
  };
  fetchTripsStops();

  return () => {
    try {
      bar1.destroy();
      bar2.destroy();
      chart3.destroy();
    } catch (e) {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [devices, positions, loading, theme]);

  // ---- render states ----
  if (loading) {
    return (
      <LoaderContainer>
        <div>Carregando...</div>
      </LoaderContainer>
    );
  }

  if (error) {
    return (
      <LoaderContainer>
        <div>{error}</div>
      </LoaderContainer>
    );
  }

  return (
    <Root>
      <Header>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {t("reportDashboard") || "Dashboard"}
        </Typography>
        <IconButton onClick={colorMode.toggleColorMode} size="small">
          {isDark ? <LightMode /> : <DarkMode />}
        </IconButton>
      </Header>

      <Content maxWidth={false} disableGutters>
        {/* Block A: top pies (3) — forced flex row so items stretch evenly */}
        <Grid
          container
          spacing={3}
          sx={{
            mb: 1,
            display: "flex",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("deviceLastUpdate") || "Última comunicação"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar grupos (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = pieGroupsRef.current.pie1 || {};
                        exportGroupsAsCSV(groups, "ultima_comunicacao.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "pie1")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <SmallPieWrapper ref={pie1Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("positionMotion") || "Movimento"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar grupos (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = pieGroupsRef.current.pie2 || {};
                        exportGroupsAsCSV(groups, "movimento.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "pie2")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <PieWrapper ref={pie2Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("sharedAlarms") || "Alarmes"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar grupos (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = pieGroupsRef.current.pie3 || {};
                        exportGroupsAsCSV(groups, "alarmes.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "pie3")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <SmallPieWrapper ref={pie3Ref} />
            </ChartContainer>
          </Grid>
        </Grid>

        {/* Block B: bottom bars (3) — same approach so bars fill full width evenly */}
        <Grid
          container
          spacing={3}
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("positionOdometer") || "Hodômetro (Kms)"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar dados (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = { Odometer: barGroupsRef.current.bar1 || [] };
                        exportGroupsAsCSV(groups, "hodometro.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "bar1")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <BarWrapper ref={bar1Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("positionHours") || "Horímetro (Horas)"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar dados (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = { Horas: barGroupsRef.current.bar2 || [] };
                        exportGroupsAsCSV(groups, "horas.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                  <MuiTooltip title="Exportar dados do gráfico">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, "bar2")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <BarWrapper ref={bar2Ref} />
            </ChartContainer>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ display: "flex", flex: "1 1 0", minWidth: 0 }}>
            <ChartContainer sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <ChartTitle>{t("reportTodayActivity") || "Atividade hoje"}</ChartTitle>
                <div>
                  <MuiTooltip title="Exportar dados (CSV)">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const groups = { Atividade: barGroupsRef.current.bar3 || [] };
                        exportGroupsAsCSV(groups, "atividade_hoje.csv");
                      }}
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </div>
              </div>
              <BarWrapper ref={bar3Ref} />
            </ChartContainer>
          </Grid>
        </Grid>

        {/* Menu de exportação */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => exportChartCSV(selectedChart || "")}>
            Exportar CSV
          </MenuItem>
          <MenuItem onClick={() => exportChartXLSX(selectedChart || "")}>
            Exportar XLSX
          </MenuItem>
        </Menu>
      </Content>
    </Root>
  );
};

// ---- Theme wrapper (keeps your theme & toggle logic) ----
const DashboardWithTheme = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = React.useState<"light" | "dark">(prefersDarkMode ? "dark" : "light");

  React.useEffect(() => {
    setMode(prefersDarkMode ? "dark" : "light");
  }, [prefersDarkMode]);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
    }),
    []
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: mode === "dark" ? "#90caf9" : "#1976D2" },
          secondary: { main: mode === "dark" ? "#81c784" : "#4CAF50" },
          background: { default: mode === "dark" ? "#121212" : "#f5f5f5", paper: mode === "dark" ? "#1e1e1e" : "#ffffff" },
        },
        typography: { fontFamily: "Helvetica, Arial, sans-serif" },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Dashboard />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default DashboardWithTheme;